'use client';

import { Skeleton } from '@mui/material';
import { useMemo, useState } from 'react';

import {
  useGetJobPerformanceQuery,
  useGetJobQuery,
} from '@/features/jobs/api/jobsApi';
import { RetainerCycleSwitcher } from '@/ui/atomic-organisms';
import { EmptyState } from '@web/ui';

import { DeliverableDriftSection } from './DeliverableDriftSection';
import styles from './JobDetailsPerformanceView.module.scss';
import { PerformanceOverviewSection } from './PerformanceOverviewSection';
import { ServiceTypeBreakdownSection } from './ServiceTypeBreakdownSection';

import type { JobKind, RetainerCycle } from '@/features/jobs/types';

type JobDetailsPerformanceViewProps = {
  jobId: string;
  jobKind: JobKind;
};

function PerformancePageSkeleton() {
  return (
    <div className={styles.performanceLoading}>
      <Skeleton variant="rectangular" height={210} />
      <Skeleton variant="rectangular" height={320} />
      <Skeleton variant="rectangular" height={300} />
    </div>
  );
}

export function JobDetailsPerformanceView({
  jobId,
  jobKind,
}: JobDetailsPerformanceViewProps) {
  const isRetainer = jobKind === 'retainer';
  const [selectedRetainerCycleId, setSelectedRetainerCycleId] = useState<
    string | null
  >(null);

  const { data: jobResponse } = useGetJobQuery(jobId, {
    skip: !jobId || !isRetainer,
  });
  const retainerCycles = useMemo<RetainerCycle[]>(
    () => (isRetainer ? (jobResponse?.data?.retainer_cycles ?? []) : []),
    [isRetainer, jobResponse?.data?.retainer_cycles],
  );

  const activeRetainerCycleId = useMemo(() => {
    if (retainerCycles.length === 0) {
      return null;
    }
    if (
      selectedRetainerCycleId &&
      retainerCycles.some((cycle) => cycle.id === selectedRetainerCycleId)
    ) {
      return selectedRetainerCycleId;
    }

    return (
      retainerCycles.find((cycle) => cycle.is_current)?.id ??
      retainerCycles[0]?.id ??
      null
    );
  }, [retainerCycles, selectedRetainerCycleId]);

  const { data, isLoading, isFetching, isError, error } =
    useGetJobPerformanceQuery(
      {
        jobId,
        retainerCycleId:
          isRetainer && activeRetainerCycleId
            ? activeRetainerCycleId
            : undefined,
      },
      {
        skip: !jobId,
        refetchOnMountOrArgChange: true,
      },
    );

  if (isLoading || (isFetching && !data?.data)) {
    return <PerformancePageSkeleton />;
  }

  if (isError) {
    return (
      <EmptyState
        title="Performance unavailable"
        description={
          ((error as { data?: { message?: string } } | undefined)?.data
            ?.message as string | undefined) ||
          'Unable to load performance metrics right now.'
        }
      />
    );
  }

  const payload = data?.data;
  if (!payload) {
    return (
      <EmptyState
        title="Performance unavailable"
        description="No performance metrics are available for this job yet."
      />
    );
  }

  return (
    <section className={styles.performancePage}>
      {isRetainer && retainerCycles.length > 0 ? (
        <div className={styles.performanceTopBar}>
          <RetainerCycleSwitcher
            cycles={retainerCycles}
            selectedCycleId={activeRetainerCycleId}
            onChange={(cycleId) => setSelectedRetainerCycleId(cycleId)}
          />
        </div>
      ) : null}

      <PerformanceOverviewSection
        overview={payload.overview}
        changeOrderSummary={payload.change_order_summary}
      />
      <DeliverableDriftSection
        rows={payload.deliverable_drift}
        overview={payload.overview}
      />
      <ServiceTypeBreakdownSection
        rows={payload.service_type_breakdown}
        overview={payload.overview}
      />
    </section>
  );
}
